import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import ConfigContext from '../../utils/ConfigContext/ConfigContext';
import { getNormalizedApiData } from '../../utils/ApiHelpers';
import FloorPlanPrintPageNormalizedData from './FloorPlanPrintPageNormalizedData';
import { createBedBathSqFtTableData } from '../../utils/DrupalEntityTypes/FloorPlan/FloorPlanHelpers';

import DetailTableInfo from '../Detail/DetailTable';
import FloorPlanPrintIndividualPage from './FloorPlanPrintIndividualPage';
import FloorPlanFinancialPlans from '../FloorPlan/FloorPlanFinancialPlans';

import './FloorPlanPrintPage.scss';

const FloorPlanPrintPage = () => {
  const [ floorPlanData, setFloorPlanData ] = useState( {} );
  const [ tableInfo, setTableInfo ] = useState( [] );
  const [ pricingInfo, setPricingInfo ] = useState( [] );
  const [ threeDLoaded, setThreeDLoaded ] = useState( false );
  const [ twoDLoaded, setTwoDLoaded ] = useState( false );
  const [ showPricing, setShowPricing ] = useState( false );

  const { drupalNid } = useParams();

  const { featureFlags } = useContext( ConfigContext );

  // Should we show pricing?
  useEffect( () => {
    setShowPricing( featureFlags?.platformShowPricing );
  }, [ featureFlags ] );

  // Get floor plan and community data from floor plan,
  // even though some is available in config context.
  useEffect( () => {
    const fetchData = async () => {
      const includes = [
        'og_audience',
        'og_audience.field_logo',
        'field_2d_floor_plan',
        'field_3d_floor_plan',
        'field_downloadable_pdf',
        'field_level_of_care',
        'field_floor_plan_category',
        'field_fee_groups',
        'field_fee_groups.paragraph_type',
        'field_fee_groups.field_financial_plans',
      ];
      const request = {
        entityType: 'node',
        contentType: 'floor_plan',
        params: {
          include: includes.join( ',' ),
        },
        filter: {
          nid: drupalNid,
        },
      };
      const rawData = await getNormalizedApiData(
        request,
        null,
        FloorPlanPrintPageNormalizedData,
      );
      setFloorPlanData( rawData );
    };
    if ( drupalNid ) {
      fetchData();
    }
  }, [ drupalNid ] );

  const {
    title,
    description,
    roomData,
    threeD,
    twoD,
    fees,
    feePlanMap,
  } = floorPlanData || {};

  const threeDSrc = threeD?.modal;
  const twoDSrc = twoD?.modal;

  // If a floor plan has pricing data and can show that data, set context to show
  // that the floor plan has fees.
  const hasRoomFees = fees?.length > 0;
  const hasFeePlanMap = feePlanMap && Object.keys( feePlanMap ).length > 0;

  // Determine which fee display component to show
  const renderFeeDisplay = () => {
    if ( !showPricing ) {
      return null;
    }

    if ( hasFeePlanMap ) {
      return (
        <FloorPlanFinancialPlans
          planMap={feePlanMap}
          className="detailSection detailSection__displayFees"
        />
      );
    }

    // Convert fees to a plan map if we have fees but no plan map
    if ( hasRoomFees && fees.length > 0 ) {
      try {
        // Identify entrance fee and monthly fee
        const entranceFee = fees.find( ( fee ) => fee.label === 'Entrance Fee' );
        const monthlyFee = fees.find( ( fee ) => fee.label === 'Monthly Fee' );

        if ( entranceFee || monthlyFee ) {
          // Create a synthetic plan map
          const syntheticPlanMap = {
            default: {
              planId: 'default',
              planName: null,
              entranceFee: entranceFee ? {
                formatted: entranceFee.data,
              } : null,
              entranceSecondFee: entranceFee?.note ? {
                formatted: entranceFee.note.replace( 'Second Person: ', 'Second Person: ' ),
              } : null,
              entranceSecondPerson: !!entranceFee?.note,
              monthlyFee: monthlyFee ? {
                formatted: monthlyFee.data,
              } : null,
              monthlySecondFee: monthlyFee?.note ? {
                formatted: monthlyFee.note.replace( 'Second Person: ', 'Second Person: ' ),
              } : null,
              monthlySecondPerson: !!monthlyFee?.note,
            },
          };

          return (
            <FloorPlanFinancialPlans
              planMap={syntheticPlanMap}
              className="detailSection detailSection__displayFees"
            />
          );
        }
      } catch ( error ) {
        console.error( 'Error creating synthetic plan map:', error );
      }

      // Fallback to basic table if synthetic plan creation fails
      return (
        <DetailTableInfo
          tableInfo={fees}
          className="detailSection detailSection__displayFees"
        />
      );
    }

    return null;
  };

  // Format pricing data for main table.
  useEffect( () => {
    const rawTableInfo = createBedBathSqFtTableData( floorPlanData );
    setTableInfo( rawTableInfo );
  }, [ floorPlanData ] );

  // Set images as loaded if they don't exist.
  const hasFloorPlanData = Object.keys( floorPlanData ).length > 0;
  useEffect( () => {
    if ( hasFloorPlanData ) {
      if ( !threeDSrc ) {
        setThreeDLoaded( true );
      }
      if ( !twoDSrc ) {
        setTwoDLoaded( true );
      }
    }
  }, [
    hasFloorPlanData, threeDSrc, twoDSrc,
  ] );

  // Don't open print dialog until fp images have loaded.
  useEffect( () => {
    if ( twoDLoaded && threeDLoaded ) {
      window.print();
    }
  }, [ twoDLoaded,
    threeDLoaded ] );

  return (
    <div className="floorPlanPrintPage">

      { threeDSrc && (
        <FloorPlanPrintIndividualPage
          floorPlanData={floorPlanData}
        >
          <img
            alt={`${title} 3D Floor Plan`}
            className="floorPlanPrintPage__image"
            src={threeDSrc}
            onLoad={() => {
              if ( !threeDLoaded ) {
                setThreeDLoaded( true );
              }
            }}
          />
        </FloorPlanPrintIndividualPage>
      )}

      { twoDSrc && (
        <FloorPlanPrintIndividualPage
          floorPlanData={floorPlanData}
        >
          <img
            alt={`${title} 2D Floor Plan`}
            className="floorPlanPrintPage__image"
            src={twoDSrc}
            onLoad={() => {
              if ( !twoDLoaded ) {
                setTwoDLoaded( true );
              }
            }}
          />
        </FloorPlanPrintIndividualPage>
      )}

      { ( tableInfo?.length || pricingInfo?.length || description || roomData ) && (
        <FloorPlanPrintIndividualPage
          floorPlanData={floorPlanData}
        >
          <DetailTableInfo
            tableInfo={tableInfo}
            tableDescription={description}
          />

          {renderFeeDisplay()}

          <DetailTableInfo
            tableInfo={roomData}
          />

          <div className="floorPlanPrintPage__notes">
            <h2 className="floorPlanPrintPage__notesTitle">Notes</h2>

            { Array.from( { length: 20 }, ( x, i ) => <div key={`line${i}`} className="floorPlanPrintPage__notesLine" /> )}

          </div>

        </FloorPlanPrintIndividualPage>
      )}

    </div>
  );
};

export default FloorPlanPrintPage;
